import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import i2Math from '@/utils/i2Math';
import { fetchServiceCompletionRateSummary } from '@/redux/dashboard/getServiceCompletionRateSummary/serviceCompletionRateSummarySlice';
import CustomerSatisfaction from './CustomerSatisfaction';

const DEPTS = [
  { name: '产康当日完成率', color: '#00e5ff', dataKey: 'postpartumCare' },
  { name: '护理当日完成率', color: '#00d4aa', dataKey: 'nursingCare' },
  { name: '客房当日完成率', color: '#fbbf24', dataKey: 'housekeeping' },
  { name: '餐饮当日完成率', color: '#00e5ff', dataKey: 'catering' },
  { name: '到家当日完成率', color: '#ff6b6b', dataKey: 'homeService' }
];

function toPercent(raw) {
  if (raw == null || raw === '') return 0;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n) || n === 0) return 0;
  const decimal = n > 1 ? i2Math.i2Division(n, 100) : n;
  return i2Math.i2Multiply(decimal, 100);
}

function CompletionStats({ storeId }) {
  const dispatch = useDispatch();
  const serviceCompletionRateSummary = useSelector((state) => state.dashboard?.serviceCompletionRateSummary);
  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const pull = () => {
      dispatch(fetchServiceCompletionRateSummary({
        query: { store_id: selectedStoreId, date: today }
      })).unwrap().catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 30000);
    return () => clearInterval(timer);
  }, [dispatch, selectedStoreId, today]);

  const sameStore = String(serviceCompletionRateSummary?.query?.store_id ?? '') === selectedStoreId;
  const data = sameStore ? (serviceCompletionRateSummary?.data || {}) : {};
  const avgRate = Math.round(toPercent(data.overallCompletion));

  return (
    <div className="completion-stats">
      <div className="stats-glow-bg"></div>
      <div className="completion-header">
        <div className="header-left">
          <span className="completion-title">各部门当日完成率</span>
          <span className="header-divider"></span>
          <span className="header-stats">
            <span className="stats-item">
              <span className="stats-num">{avgRate}%</span>
              <span className="stats-label">平均</span>
            </span>
          </span>
        </div>
        <div className="header-right">
          <span className="live-indicator">
            <span className="live-ring"></span>
            <span className="live-dot-inner"></span>
          </span>
          <span className="completion-time">实时数据流</span>
        </div>
      </div>
      <div className="completion-main">
        {DEPTS.map((dept) => {
          const rate = toPercent(data[dept.dataKey]);
          const statusClass = rate >= 90 ? 'status-good' : (rate >= 75 ? 'status-normal' : 'status-warning');
          return (
            <div key={dept.dataKey} className={`completion-item ${statusClass}`}>
              <div className="item-corner-tl"></div>
              <div className="item-corner-tr"></div>
              <div className="item-corner-bl"></div>
              <div className="item-corner-br"></div>
              <div className="item-glow" style={{ background: dept.color }}></div>
              <div className="completion-label">
                <span className="label-dot" style={{ background: dept.color }}></span>
                {dept.name}
              </div>
              <div className="completion-inline">
                <div className="completion-value" style={{ color: dept.color }}>
                  <span className="value-num">{Math.round(rate)}</span>
                  <span className="value-unit">%</span>
                </div>
                <div className="completion-bar-wrapper">
                  <div className="bar-bg-lines">
                    <span></span><span></span><span></span><span></span><span></span>
                  </div>
                  <div
                    className="completion-bar"
                    style={{
                      width: `${rate}%`,
                      background: `linear-gradient(90deg, ${dept.color}40, ${dept.color})`,
                      boxShadow: `0 0 10px ${dept.color}50, inset 0 0 8px ${dept.color}30`
                    }}
                  >
                    <div className="bar-shine"></div>
                  </div>
                  <div
                    className="completion-bar-glow"
                    style={{
                      left: `${rate}%`,
                      backgroundColor: dept.color,
                      boxShadow: `0 0 10px ${dept.color}, 0 0 20px ${dept.color}80, 0 0 30px ${dept.color}50`
                    }}
                  ></div>
                </div>
              </div>
              <div className="item-status-line" style={{ background: `linear-gradient(90deg, ${dept.color}, transparent)` }}></div>
            </div>
          );
        })}
        <CustomerSatisfaction storeId={storeId} />
      </div>
      <div className="stats-bottom-bar">
        <div className="bottom-line"></div>
        <div className="bottom-dots">
          <span></span><span></span><span></span><span></span><span></span>
        </div>
      </div>
    </div>
  );
}

export default CompletionStats;
